/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import AsyncSelect from "react-select/async";
import { useTranslation } from "react-i18next";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";
import { getCustomStyles } from "Config/Config";
import { getMemberOptions } from "helper/ContactHelper/ContactHelper";

const SaveHeartRatePopup = ({
  afterActionClose = () => {},
  heartRateId,
  setHeartRateId,
}) => {
  const { t } = useTranslation();
  const token = localStorage.getItem("token");

  // States for input fields
  const [heartRate, setHeartRate] = useState("");
  const [heartRateDate, setHeartRateDate] = useState(""); // New state for date
  const [assignTo, setAssignTo] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] =
    useState(false); /* New state for loading heart rate details */
  const [errors, setErrors] = useState({});
  const offcanvasBodyRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};
    if (!heartRate) {
      newErrors.heartRate = t("Please enter heart rate value!");
    } else if (isNaN(heartRate) || heartRate <= 0) {
      newErrors.heartRate = t("Please enter a valid heart rate number!");
    }
    if (!heartRateDate) {
      // Validation for new date field
      newErrors.heartRateDate = t("Please enter a date!");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Async load options for Assign To (from AddAllergyModal)
  const loadAssineeOptions = async (inputValue) => {
    try {
      const options = await getMemberOptions(inputValue);
      return options;
    } catch (error) {
      return [];
    }
  };

  //function for get heart rate details
  const getHeartRateDetails = async () => {
    try {
      setIsLoading(true); /* Set loading to true before API call */
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_HEART_RATE_BY_ID +
        `/${heartRateId}?token=${token}`;

      const response = await getData(requestUrl);

      if (response.status) {
        const heartRateData = response.data;

        setHeartRate(heartRateData.bpm);
        setHeartRateDate(
          heartRateData.date
            ? new Date(heartRateData.date).toISOString().split("T")[0]
            : ""
        ); // Set date from API data
        setAssignTo(heartRateData?.uservalue);
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsLoading(
        false
      ); /* Set loading to false after API call (success or failure) */
    }
  };

  const saveHeartRateHandler = async () => {
    if (!validateForm()) {
      if (offcanvasBodyRef.current) {
        offcanvasBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    try {
      setIsSaving(true);
      const apiData = {
        bpm: parseInt(heartRate),
        date: heartRateDate, // Include date in API data
        user: assignTo ? assignTo?.value : null,
      };

      let requestUrl = url.API_BASE_URL;
      let response = {};

      if (heartRateId) {
        requestUrl +=
          url.API_UPDATE_HEART_RATE + `/${heartRateId}?token=${token}`;
        response = await putData(requestUrl, apiData);
      } else {
        requestUrl += url.API_CREATE_HEART_RATE + `?token=${token}`;
        response = await postData(requestUrl, apiData);
      }

      console.log("response in save heart rate", response);

      setIsSaving(false);

      if (response.status) {
        resetHandler();
        window.bootstrap.Offcanvas.getInstance(
          document.querySelector("#offcanvasSaveHeartRate")
        ).hide();
        afterActionClose([response.data]);
      }
    } catch (error) {
      console.error("Error saving heart rate:", error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const resetHandler = () => {
    setHeartRate("");
    setHeartRateDate(""); // Clear date on reset
    setAssignTo(null);
    setErrors({});
    setHeartRateId(null);
  };

  useEffect(() => {
    if (heartRateId) {
      getHeartRateDetails();
    }
  }, [heartRateId]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0"
      tabIndex="-1"
      id="offcanvasSaveHeartRate"
      aria-labelledby="offcanvasSaveHeartRateLabel"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pe-4 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-icons-outlined text-primary fs-5 icon-md">
              monitor_heart
            </span>
          </div>
          <div>
            <h3
              className="offcanvas-title fw-bold text-white mb-0"
              id="offcanvasSaveHeartRateLabel"
            >
              Save Heart Rate
            </h3>
            <p className="text-white fs-sm mb-0 mt-2">
              Enter and save heart rate readings
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-1 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      <div className="offcanvas-body p-4" ref={offcanvasBodyRef}>
        {isLoading ? (
          <div className="loading-state text-center py-5">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted">{t("Loading heart rate responses...")}</p>
          </div>
        ) : null}

        {!isLoading && (
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm bg-light">
            <form className="d-flex flex-column h-100">
              <div className="fields-container flex-fill">
                {/* Heart Rate input */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="form-group p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="heartRate"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Heart Rate (bpm) *
                      </label>
                      <input
                        type="number"
                        id="heartRate"
                        className={`form-control fs-sm rounded-lg-2 shadow-none ${
                          errors.heartRate ? "is-invalid" : ""
                        }`}
                        placeholder="e.g., 70"
                        value={heartRate}
                        onChange={(e) => {
                          setHeartRate(e.target.value);
                          if (errors.heartRate) {
                            setErrors((prev) => ({ ...prev, heartRate: "" }));
                          }
                        }}
                      />
                      {errors.heartRate && (
                        <div className="invalid-feedback">
                          {errors.heartRate}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Date input */}
                  <div className="col-md-6">
                    <div className="form-group p-3 border rounded-8 shadow-sm bg-white">
                      <label
                        htmlFor="heartRateDate"
                        className="d-block fs-sm fw-semibold mb-2 text-dark"
                      >
                        Date *
                      </label>
                      <input
                        type="date"
                        id="heartRateDate"
                        className={`form-control fs-sm rounded-lg-2 shadow-none ${
                          errors.heartRateDate ? "is-invalid" : ""
                        }`}
                        value={heartRateDate}
                        onChange={(e) => {
                          setHeartRateDate(e.target.value);
                          if (errors.heartRateDate) {
                            setErrors((prev) => ({
                              ...prev,
                              heartRateDate: "",
                            }));
                          }
                        }}
                      />
                      {errors.heartRateDate && (
                        <div className="invalid-feedback">
                          {errors.heartRateDate}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Assign To field */}
                <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Assign To
                  </label>
                  <AsyncSelect
                    isClearable
                    name="assignTo"
                    loadOptions={loadAssineeOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    placeholder={t("Select Assignee")}
                    styles={getCustomStyles()}
                    value={assignTo}
                    onChange={setAssignTo}
                  />
                </div>
              </div>

              {/* Heart Rate Interpretation Info */}
              <div className="p-4 border rounded-10 shadow-sm bg-gradient-light position-relative overflow-hidden">
                <div className="row align-items-center mb-3">
                  <div className="col">
                    <h5 className="fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                      <span className="material-icons-outlined text-primary">
                        assessment
                      </span>
                      Heart Rate Interpretation
                    </h5>
                    <p className="text-muted fs-sm mb-0">
                      Reference ranges for adults
                    </p>
                  </div>
                </div>

                <div className="interpretation-grid">
                  <div className="interpretation-item mb-3 p-3 bg-white rounded-8 border-start border-4 border-primary">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <div
                        className="status-dot bg-primary rounded-circle"
                        style={{ width: "8px", height: "8px" }}
                      ></div>
                      <span className="fw-semibold text-primary fs-sm">
                        Normal
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-3 text-dark fs-sm">
                      <span>
                        <strong>Heart Rate:</strong> 60-100 bpm
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-top border-gray-200">
                  <p className="text-muted fs-xs mb-0 d-flex align-items-center gap-1">
                    <span className="material-icons-outlined fs-6">info</span>
                    Consult your healthcare provider for proper interpretation
                    and treatment recommendations.
                  </p>
                </div>
              </div>

              <div className="action d-flex justify-content-end pt-3 border-top mt-auto">
                <button
                  type="button"
                  className="btn btn-primary d-flex align-items-center gap-2"
                  onClick={saveHeartRateHandler}
                  style={{ cursor: isSaving ? "not-allowed" : "pointer" }}
                  disabled={isSaving ? true : false}
                >
                  {isSaving ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {t("Saving...")}
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined me-1">
                        save
                      </span>{" "}
                      {t("Save Heart Rate")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveHeartRatePopup;
